import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Endpoint } from '@/Config/api/endpoint';
import { QueryKey } from '@/Config/api/queryKey';
import { KnowledgeBaseApi } from '@/Domain/api/KnowledgeBase/KnowledgeBase.api';
import { ArticleDto } from '@/Domain/api/KnowledgeBase/dto/Article.dto';
import { ErrorType } from '@/Domain/components/KnowledgeBaseArticles/types';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useArticleLimitCheck } from '@/Domain/hooks/KnowledgeBase/useArticleLimitCheck.hook';

type FileInput = {
  file: File;
};

export const useUploadArticles = () => {
  const queryClient = useQueryClient();
  const handleArticleLimitCheck = useArticleLimitCheck();
  const { capture } = usePostHogAnalytics();

  const mutation = useMutation<
    { article: ArticleDto },
    AxiosError<{ errors?: ErrorType<FileInput> }>,
    File
  >({
    mutationKey: [Endpoint.knowledgeBase.articles.uploadFile],
    mutationFn: KnowledgeBaseApi.uploadFile,
    onSuccess: ({ article }) => {
      queryClient.setQueryData<ArticleDto[]>(
        [QueryKey.knowledgeBase.articles.listOrFilter, '', []],
        (oldData = []) => [article, ...oldData],
      );

      void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.onboarding.progress] });
    },
    onError: (error) => {
      console.error('Error uploading file:', error);
    },
  });

  const handleUploadFile = async (file: File) => {
    try {
      mutation.mutate(file);
      return true;
    } catch (error: any) {
      console.error('Error uploading file:', error);
      return false;
    }
  };

  const handleUploadFiles = async (files: File[], onComplete?: () => void) => {
    capture(PostHogEventEnum.ArticleUploadStarted, {
      upload_type: 'file',
      file_count: files.length,
    });
    try {
      handleArticleLimitCheck(async () => {
        await Promise.all(files.map((file) => handleUploadFile(file)));
        if (onComplete) {
          onComplete();
        }
      });
    } catch (error) {
      console.error('Error uploading files:', error);
    }
  };

  return {
    handleUploadFile,
    handleUploadFiles,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};
